/**
 * Registry of RPA-routed eligibility payers.
 *
 * Single source of truth shared between frontend and backend for:
 *  - Which payers route to a portal scraper lane instead of Stedi 270/271
 *  - How to recognize them (TP-ID sentinel OR payer-name substring)
 *  - The per-vendor clinic integration key (clinic.integrations[key])
 *  - The stable vendor ID — this value is ALSO written as the check doc's
 *    `type` field, so adding a new vendor here immediately makes RPA Jobs
 *    dashboard queries `where('type', '==', '<vendorId>')` pick it up.
 *
 * Adding a new RPA vendor:
 *  1. Add a new `RpaVendorId` literal below.
 *  2. Add its metadata entry to `RPA_VENDORS`.
 *  3. (Backend) Register its job inputs + benefits parser in
 *     `functions/src/eligibilityCheck/rpaVendorsBackend.ts`, and its lane in
 *     `checkLaneDispatch.service.ts`.
 *  4. (Frontend) Add a logo asset and extend the logo map in
 *     `src/constants/hardcodedPayers.ts`.
 */

export type RpaVendorId = "vsp" | "eyemed" | "versant";

export interface RpaVendorConfig {
  /** Stable vendor identifier. ALSO the `type` value on check docs. */
  id: RpaVendorId;
  /** Human-readable name shown in the UI and on PayerSearch dropdowns. */
  displayName: string;
  /**
   * TP-ID sentinel surfaced by the frontend's hardcoded PayerSearch entries.
   * Never sent to Stedi — the router picks it up first. Must be unique.
   */
  tpIdSentinel: string;
  /**
   * Case-insensitive substrings matched against the Revolution-supplied
   * payer name. Used when only a free-form name is available (scheduled
   * sweep / background processing). Kept as an array to handle common
   * variants like "eyemed" / "eye med".
   */
  payerNamePatterns: readonly string[];
  /**
   * Key under `clinic.integrations.{integrationKey}` — the clinic's record
   * for this vendor's portal. A clinic is configured for the vendor's lane
   * when the record exists and carries `secretName`
   * (`shared/credentialSecrets.ts` `integrationConfigured`).
   */
  integrationKey: string;
  /**
   * The insurance-search entries, when the vendor has more than one name in
   * the dropdown (Versant: Davis Vision and Superior Vision, one portal).
   * Absent → one entry, the vendor's own name and sentinel. Each entry's
   * sentinel routes to this vendor like its own.
   */
  searchEntries?: ReadonlyArray<{name: string; tpIdSentinel: string}>;
}

export const RPA_VENDORS: ReadonlyArray<RpaVendorConfig> = [
  {
    id: "vsp",
    displayName: "VSP",
    tpIdSentinel: "RPA_VSP",
    payerNamePatterns: ["vsp"],
    integrationKey: "vsp",
  },
  {
    id: "eyemed",
    displayName: "EyeMed",
    tpIdSentinel: "RPA_EYEMED",
    payerNamePatterns: ["eyemed", "eye med"],
    integrationKey: "eyemed",
  },
  {
    // Versant Health's portal answers for its Davis Vision and Superior
    // Vision brands, and for MetLife's vision plans. Routing reads the name,
    // never a payer code: 00010 is also VSP's.
    id: "versant",
    displayName: "Versant",
    tpIdSentinel: "RPA_VERSANT",
    payerNamePatterns: ["davis vision", "superior vision", "met life", "metlife"],
    integrationKey: "versant",
    searchEntries: [
      {name: "Davis Vision", tpIdSentinel: "RPA_VERSANT_DAVIS"},
      {name: "Superior Vision", tpIdSentinel: "RPA_VERSANT_SUPERIOR"},
    ],
  },
];

/**
 * Deterministic lookup by TP-ID sentinel. Use this on the manual path
 * where the UI hands us a hardcoded sentinel — routing is unambiguous.
 */
export function rpaVendorByTpId(
  tpId: string | null | undefined,
): RpaVendorConfig | null {
  if (!tpId) return null;
  return RPA_VENDORS.find((v) =>
    v.tpIdSentinel === tpId || (v.searchEntries ?? []).some((e) => e.tpIdSentinel === tpId),
  ) ?? null;
}

/** Escape regex metacharacters so a payer-name pattern matches literally. */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Fuzzy lookup by payer name — anchored on WORD BOUNDARIES, not a raw
 * substring. `"vsp"` matches "Patient VSP Plan" but NOT "Crvspine"; `"eyemed"`
 * matches "EyeMed Vision" but NOT "United EyeMedical" (the substring misroute
 * the old `includes()` produced). Used on the Revolution / scheduled-sweep path
 * where only a free-form string is available.
 *
 * A per-clinic alias is consulted before this (see
 * `PayerMappingService.resolveRoute`), so this only runs for un-aliased names.
 */
export function rpaVendorByPayerName(
  name: string | null | undefined,
): RpaVendorConfig | null {
  const lower = name?.trim().toLowerCase() ?? "";
  if (!lower) return null;
  return (
    RPA_VENDORS.find((v) =>
      v.payerNamePatterns.some((p) =>
        new RegExp(`\\b${escapeRegExp(p)}\\b`).test(lower),
      ),
    ) ?? null
  );
}

/**
 * Preferred resolver: TP-ID first (deterministic), payer-name second
 * (fuzzy). Returns `null` when the payer should route to Stedi.
 */
export function resolveRpaVendor(opts: {
  tradingPartnerServiceId?: string | null;
  payerName?: string | null;
}): RpaVendorConfig | null {
  return (
    rpaVendorByTpId(opts.tradingPartnerServiceId) ??
    rpaVendorByPayerName(opts.payerName)
  );
}

/**
 * Type guard — true when `type` is a known RPA vendor ID. Used by the
 * webhook to validate that a check doc belongs to an RPA-routed vendor
 * before dispatching to the per-vendor benefits parser.
 */
export function isRpaVendorId(
  type: string | undefined | null,
): type is RpaVendorId {
  return typeof type === "string" && RPA_VENDORS.some((v) => v.id === type);
}

/** Get a vendor by ID, or null if not found. */
export function getRpaVendor(
  id: string | null | undefined,
): RpaVendorConfig | null {
  if (!id) return null;
  return RPA_VENDORS.find((v) => v.id === id) ?? null;
}
